import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { INDEX_TYPES } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { partition } from '@src/utils/functions/utils'
import { TFunction } from 'next-i18next'

import { NUMERIC_FILTER_GENERATOR_MAP } from './numericFilterGenerators'
import { FACET_TO_OPTION_VALUE_GETTER } from './optionValuesGetter'
import {
  SUPPORTED_FACETS,
  getFacetToConfig,
  NUMERIC_FILTER_KEYS,
  FILTER_KEY_TO_FACET_KEY,
} from './searchFilterConstants'
import { Facets } from './types'

export const transformFacetsToFilterOptions = (facets: Facets, t: TFunction) => {
  const FACET_TO_CONFIG = getFacetToConfig(t)
  const filters = Object.keys(facets)
    .filter(facetValue => SUPPORTED_FACETS.has(facetValue))
    .map(facetValue => {
      const valueGetter = FACET_TO_OPTION_VALUE_GETTER[facetValue] || FACET_TO_OPTION_VALUE_GETTER.DEFAULT
      return {
        ...FACET_TO_CONFIG[facetValue],
        values: valueGetter(facets[facetValue]),
      }
    })
    .filter(filter => filter.values.length > 1)
    .sort((a, b) => a.priority - b.priority)
  return filters
}

const getSelectedFilterCacheKey = (selectedFilters: SelectedFilter) => {
  const filterValuesArray: string[] = []
  Object.keys(selectedFilters).forEach(filterKey => {
    if (selectedFilters[filterKey as keyof SelectedFilter]) {
      selectedFilters[filterKey as keyof SelectedFilter]?.forEach((value: string | number) => {
        filterValuesArray.push(`${filterKey}_${value}`)
      })
    }
  })
  filterValuesArray.sort()
  return filterValuesArray.join('_')
}

export const getSearchResultsCacheKey = ({
  indexKey,
  sq,
  page,
  selectedFilters,
  sortBy,
}: {
  indexKey: INDEX_TYPES
  sq: string
  page: number
  selectedFilters?: SelectedFilter
  sortBy?: string
}): string => {
  const selectedFilterKey = selectedFilters ? getSelectedFilterCacheKey(selectedFilters) : ''
  return `${indexKey}/${sq}/${page}/${selectedFilterKey}/${sortBy}`
}

const createNumericFilters = (numericFilterKeys: string[], selectedFilters: SelectedFilter) => {
  return numericFilterKeys.reduce((acc: string[], filterKey) => {
    if (selectedFilters[filterKey as keyof SelectedFilter]) {
      const numericFilterValueGetter = NUMERIC_FILTER_GENERATOR_MAP[filterKey]
      if (numericFilterValueGetter) {
        return [...acc, ...numericFilterValueGetter(selectedFilters[filterKey as keyof SelectedFilter])]
      }
    }
    return acc
  }, [])
}

const createFacetFilers = (facetFilterKeys: string[], selectedFilters: SelectedFilter) => {
  return facetFilterKeys.reduce((acc: string[][], filterKey) => {
    if (selectedFilters[filterKey as keyof SelectedFilter]) {
      const currentFilterArray: string[] = []
      selectedFilters[filterKey as keyof SelectedFilter]?.forEach(value => {
        const facetFilterKey = FILTER_KEY_TO_FACET_KEY[filterKey] || filterKey
        currentFilterArray.push(`${facetFilterKey}:${value}`)
      })
      return [...acc, currentFilterArray]
    }
    return acc
  }, [])
}

export const getAlgoliaFilter = (selectedFilters?: SelectedFilter) => {
  let facetFilters: string[][] = []
  let numericFilters: string[] = []

  if (selectedFilters === undefined) {
    return { facetFilters, numericFilters }
  }
  const [numericFilterKeys, facetFilterKeys] = partition(Object.keys(selectedFilters), key =>
    NUMERIC_FILTER_KEYS.includes(key),
  )

  numericFilters = createNumericFilters(numericFilterKeys, selectedFilters)
  facetFilters = createFacetFilers(facetFilterKeys, selectedFilters)

  return {
    facetFilters,
    numericFilters,
  }
}
